import React from "react"
import DOMPurify from "dompurify"
import { Empty, Spin, Typography } from "@mimber/ui-web"
import { useCommunicationTemplate } from "@mimber/client/mimber-organization"
import { renderTemplateVariables } from "@mimber/schema"
import { SAMPLE_VARIABLE_CONTEXT } from "@/components/modals/AnnouncementPreviewModal/AnnouncementPreviewModal.utils"
import type { AnnouncementPreviewProps } from "./AnnouncementPreview.types"

const { Text } = Typography

export const AnnouncementPreview: React.FunctionComponent<
  AnnouncementPreviewProps
> = ({ templateUuid, emptyText = "No template assigned" }) => {
  const { data, isLoading, isError } = useCommunicationTemplate(
    templateUuid ?? ""
  )

  if (!templateUuid) {
    return <Empty description={emptyText} />
  }

  if (isLoading) {
    return (
      <div
        style={{ textAlign: "center", padding: 48 }}
        data-testid="announcement-preview-loading"
      >
        <Spin size="large" />
      </div>
    )
  }

  if (isError) {
    return (
      <Empty
        description="Failed to load template preview"
        data-testid="announcement-preview-error"
      />
    )
  }

  const template = data?.data

  if (!template) {
    return <Empty description="Template not found" />
  }

  return (
    <div data-testid="announcement-preview-content">
      {template.subject && (
        <div style={{ marginBottom: 12 }}>
          <Text strong>Subject: </Text>
          <Text>
            {renderTemplateVariables(template.subject, SAMPLE_VARIABLE_CONTEXT)}
          </Text>
        </div>
      )}
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            renderTemplateVariables(template.content, SAMPLE_VARIABLE_CONTEXT)
          ),
        }}
      />
      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Variable placeholders (e.g. {"{{member_name}}"}) are shown with sample
          values. Actual values will be substituted per recipient at send time.
        </Text>
      </div>
    </div>
  )
}
